import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { NotificationService } from 'src/app/services/notification.service';
import { finalize } from 'rxjs';
import { ProgramService } from '../program.service';

interface TimeSlot {
    hour: string;
    minute: string;
    day: number;
    added?: boolean;
    deleted?: boolean;
    id?: number;
}

@Component({
    selector: 'app-configurare-sloturi',
    templateUrl: './configurare-sloturi.component.html',
    styleUrls: ['./configurare-sloturi.component.scss']
})
export class ConfigurareSloturiComponent implements OnInit {
    helper = helper;
    isLoading = false;

    selectedDayID = 1;
    daysOfWeek = [
        { id_day: 1, day: "Luni" },
        { id_day: 2, day: "Marți" },
        { id_day: 3, day: "Miercuri" },
        { id_day: 4, day: "Joi" },
        { id_day: 5, day: "Vineri" },
        { id_day: 6, day: "Sâmbătă" },
        { id_day: 0, day: "Duminică" },
    ];

    possibleHours = ['08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18'];
    possibleMinutes: string[] = [];

    timeCursor = {
        hour: '08',
        minute: '00'
    };

    timeSlots: TimeSlot[] = [];

    durataProgramare: number | null = null;

    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<ConfigurareSloturiComponent>,
        private notificationService: NotificationService,
        private programService: ProgramService
    ) { }

    ngOnInit(): void {
        this.initializeFromData();
    }

    initializeFromData() {
        if (this.data?.configuration?.durata_programare) {
            this.durataProgramare = this.data.configuration.durata_programare;
            this.generatePossibleMinutes();
        } else {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Vă rugăm să configurați programul.',
                this.errorIcon
            );

            this.closeDialog();
            return;
        }

        if (this.data.slots && Array.isArray(this.data.slots)) {
            this.timeSlots = this.data.slots.map((slot: any) => {
                const timeParts = slot.time_from.split(':');
                return {
                    hour: timeParts[0],
                    minute: timeParts[1],
                    day: slot.day_of_week,
                    id: slot.id
                };
            });
        }
    }

    generatePossibleMinutes() {
        if (!this.durataProgramare) {
            console.error('Cannot generate minutes without durata_programare');
            return;
        }

        const generatedMinutes: number[] = [];

        for (let currentValue = 0; currentValue < 60; currentValue += this.durataProgramare) {
            generatedMinutes.push(currentValue);
        }

        this.possibleMinutes = generatedMinutes.map(minut =>
            minut < 10 ? '0' + minut.toString() : minut.toString()
        );

        if (this.possibleMinutes.length > 0 && !this.possibleMinutes.includes(this.timeCursor.minute)) {
            this.timeCursor.minute = this.possibleMinutes[0];
        }
    }

    selectDay(id_day: number) {
        this.selectedDayID = id_day;

        this.timeCursor = {
            hour: '08',
            minute: this.possibleMinutes[0] || '00'
        };
    }

    addTime() {
        if (!this.durataProgramare) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Durată programare lipsă. Nu se pot adăuga sloturi.',
                this.errorIcon
            );
            return;
        }

        const newSlot: TimeSlot = {
            hour: this.timeCursor.hour,
            minute: this.timeCursor.minute,
            day: this.selectedDayID,
            added: true
        };

        const existingSlot = this.timeSlots.find(slot =>
            slot.hour === newSlot.hour &&
            slot.minute === newSlot.minute &&
            slot.day === newSlot.day &&
            !slot.deleted
        );

        if (existingSlot) {
            if (existingSlot.deleted) {
                existingSlot.deleted = false;
                this.increaseTime();
            } else {
                this.notificationService.warningSwal(
                    this.errorTitle,
                    'Ora și minutul alese sunt deja existente.',
                    this.errorIcon
                );
            }
        } else {
            this.timeSlots.push(newSlot);
            this.increaseTime();
        }
    }

    increaseTime() {
        if (!this.durataProgramare) return;

        let newMinute = parseInt(this.timeCursor.minute, 10) + this.durataProgramare;
        let newHour = parseInt(this.timeCursor.hour, 10);

        if (newMinute >= 60) {
            newMinute = 0;
            newHour += 1;

            if (newHour === 19) {
                newHour = 8;
            }
        }

        this.timeCursor = {
            hour: newHour < 10 ? '0' + newHour.toString() : newHour.toString(),
            minute: newMinute < 10 ? '0' + newMinute.toString() : newMinute.toString()
        };
    }

    removeTime(index: number) {
        const removedSlot = this.timeSlots[index];

        if (removedSlot.added === true) {
            this.timeSlots.splice(index, 1);
        } else {
            this.timeSlots[index].deleted = true;
        }
    }

    getVisibleSlotsForDay(): TimeSlot[] {
        return this.timeSlots
            .filter(slot => slot.day === this.selectedDayID && !slot.deleted)
            .sort((a, b) => {
                const aTime = parseInt(a.hour) * 60 + parseInt(a.minute);
                const bTime = parseInt(b.hour) * 60 + parseInt(b.minute);
                return aTime - bTime;
            });
    }

    closeDialog() {
        this.dialogRef.close({ success: false });
    }

    saveSloturi() {
        const activeSlotsCount = this.timeSlots.filter(slot => !slot.deleted).length;

        if (activeSlotsCount === 0) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Adăugați cel puțin un slot înainte de a salva.',
                this.errorIcon
            );
            return;
        }

        const payload = {
            timeData: this.timeSlots.map(slot => {
                const slotData: any = {
                    hour: slot.hour,
                    minute: slot.minute,
                    day: slot.day
                };

                if (slot.id) {
                    slotData.id = slot.id;
                }

                if (slot.added) {
                    slotData.added = true;
                }
                if (slot.deleted) {
                    slotData.deleted = true;
                }

                return slotData;
            })
        };

        this.isLoading = true;

        this.programService.updateSlots(this.data.id, payload)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                }
            });
    }

    getDayName(): string {
        const day = this.daysOfWeek.find(d => d.id_day === this.selectedDayID);
        return day ? day.day : '';
    }
}